import { SITE_URL } from '@blind-kings/site-config';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Coin from '../../Components/Coin';
import Gem from '../../Components/Gem';
import EmoteIcon from '../../Components/EmoteIcon';
import ProfileAvatar from '../../Components/ProfileAvatar';
import {
  FacebookIcon,
  FacebookMessengerIcon,
  FacebookMessengerShareButton,
  FacebookShareButton,
  RedditIcon,
  RedditShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import Modal from '../../Components/Modal';
import ModalButton from '../../Components/ModalButton';
import { useAuthContext } from '../../contexts/AuthContext';
import {
  GEMS_PER_LEVEL,
  GAME_MODES,
  getLevelFromXP,
  getPercentageOfLevelFromXP,
  getXPForLevel,
  type BlindKingsGame,
} from '@blind-kings/game-core';
import { formatCurrencyAmount } from '../../utils/format-currency';

export type GameRewards = {
  result?: string;
  xp?: number;
  coins?: number;
  doubleXP?: boolean;
  doubleCoins?: boolean;
  lengthMult?: number;
  modeMult?: number;
  outcomeBase?: number;
  levelUps?: number;
  levelUpCoins?: number;
  levelUpGems?: number;
  levelBefore?: number;
  levelAfter?: number;
  passXp?: number;
  eloRated?: boolean;
  eloReason?: string;
  eloBefore?: number;
  eloAfter?: number;
  eloDelta?: number;
  tier?: string;
  matchAbort?: boolean;
};

export type GameOverModalProps = {
  blindKingsGame: BlindKingsGame;
  rewards?: GameRewards;
  /** Wait for finishing move animation before showing */
  ready?: boolean;
  /** Extra ended signal (e.g. status already FINISHED). */
  ended?: boolean;
  gameId?: string;
  /** When true, reopen after the user previously dismissed (manual Results). */
  forceOpen?: boolean;
  /** Fires every time the modal is closed (auto or manual). */
  onClose?: () => void;
  onDismissedChange?: (dismissed: boolean) => void;
};

function dismissStorageKey(gameId: string) {
  return `bk-game-over-dismissed:${gameId}`;
}

function readDismissed(gameId: string | undefined) {
  if (!gameId || typeof sessionStorage === 'undefined') return false;
  try {
    return sessionStorage.getItem(dismissStorageKey(gameId)) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(gameId: string | undefined) {
  if (!gameId || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(dismissStorageKey(gameId), '1');
  } catch {
    // ignore quota / private mode
  }
}

function SectionLabel({ children }: { children: string }) {
  return (
    <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-bk-muted">
      {children}
    </div>
  );
}

function RewardTile({
  label,
  amount,
  accentClass,
  icon,
  doubled,
  reserveBoostLine,
}: {
  label: string;
  amount: number;
  accentClass: string;
  icon: ReactNode;
  doubled?: boolean;
  /** Keep tile heights aligned when the sibling shows a boost breakdown. */
  reserveBoostLine?: boolean;
}) {
  const base = doubled && amount > 0 ? Math.round(amount / 2) : amount;
  const boost = doubled ? amount - base : 0;

  return (
    <div
      className={`flex flex-1 flex-col items-center gap-1.5 border bg-bk-bg px-3 py-3 ${
        doubled ? 'border-bk-gold-dim/70' : 'border-bk-border'
      }`}
    >
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-bk-muted">
        {label}
        {doubled ? (
          <span className="bg-bk-gold/15 px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-bk-gold-bright">
            ×2
          </span>
        ) : null}
      </div>
      <div
        className={`inline-flex items-center gap-2 text-2xl font-bold tabular-nums ${accentClass}`}
      >
        +{formatCurrencyAmount(amount)} {icon}
      </div>
      {doubled && boost > 0 ? (
        <div className="text-center text-[10px] leading-snug text-bk-muted">
          <span className="tabular-nums">{formatCurrencyAmount(base)}</span>
          {' base'}
          <span className="mx-1 text-bk-gold">+</span>
          <span className="tabular-nums text-bk-gold">
            {formatCurrencyAmount(boost)}
          </span>
          {' boost'}
        </div>
      ) : reserveBoostLine ? (
        <div className="invisible text-[10px] leading-snug" aria-hidden="true">
          0 base + 0 boost
        </div>
      ) : null}
    </div>
  );
}

export default function GameOverModal({
  blindKingsGame: {
    isGameOver,
    userWon,
    firstPlayer,
    secondPlayer,
    firstPlayerWon,
    gameMode,
    userIsFirstPlayer,
  },
  rewards,
  ready = true,
  ended = false,
  gameId,
  forceOpen = false,
  onClose,
  onDismissedChange,
}: GameOverModalProps) {
  const [dismissed, setDismissed] = useState(() => readDismissed(gameId));

  useEffect(() => {
    setDismissed(readDismissed(gameId));
  }, [gameId]);

  useEffect(() => {
    onDismissedChange?.(dismissed);
  }, [dismissed, onDismissedChange]);

  const dismiss = useCallback(() => {
    writeDismissed(gameId);
    setDismissed(true);
    onClose?.();
  }, [gameId, onClose]);

  const {
    xp: xpEarned = 0,
    coins: coinsEarned = 0,
    doubleXP,
    doubleCoins,
    lengthMult,
    levelUps = 0,
    levelUpCoins = 0,
    levelUpGems = 0,
    passXp = 0,
    eloRated,
    eloReason,
    eloBefore,
    eloAfter,
    eloDelta,
    tier: rewardTier,
    matchAbort,
  } = rewards ?? {};

  const winnerSeat = firstPlayerWon ? firstPlayer : secondPlayer;
  const winnerEmote = (
    winnerSeat as { recentEmote?: { emoteId?: string } } | undefined
  )?.recentEmote?.emoteId;

  const opponentSeat = userIsFirstPlayer ? secondPlayer : firstPlayer;

  const {
    userData: { level = 1, xp: currentXP = 0 } = {},
  } = useAuthContext();

  const previousXP = currentXP - xpEarned;
  const previousLevel = getLevelFromXP(previousXP);
  const finalLevel = getLevelFromXP(currentXP);

  const levelDifference = finalLevel - previousLevel;
  const levelsGained = Math.max(levelUps, levelDifference, 0);
  const showedLevelUp = levelsGained > 0;
  const bonusGems =
    levelUpGems > 0 ? levelUpGems : levelsGained * GEMS_PER_LEVEL;
  const bonusCoins = levelUpCoins;

  const isLocal = gameMode === GAME_MODES.LOCAL_GAME;

  const lengthNotice =
    lengthMult === 0
      ? {
          title: 'Too short for rewards',
          body: 'This game ended too quickly, so no XP or coins were awarded.',
        }
      : lengthMult === 0.5
        ? {
            title: 'Short game — half rewards',
            body: 'This game was shorter than usual, so you earned less XP and coins.',
          }
        : null;

  const shareTitle = userWon
    ? `I just destroyed in a game of Blind Kings!\n\nThink you can beat me?`
    : `I played a fun game of Blind Kings!\n\nThink you can beat me?`;
  const shareUrl = `${SITE_URL}/?utm_source=share&utm_medium=social`;

  const progressBars = Math.max(levelDifference + 1, 1);

  const playAgainTo =
    gameMode === GAME_MODES.RANKED_GAME
      ? `/queue/${GAME_MODES.RANKED_GAME}`
      : gameMode === GAME_MODES.ONLINE_GAME
        ? `/queue/${GAME_MODES.ONLINE_GAME}`
        : '/play';

  const opponentName =
    (opponentSeat as { displayName?: string } | undefined)?.displayName ||
    'Opponent';
  const opponentAvatar = (
    opponentSeat as { avatarId?: string } | undefined
  )?.avatarId;
  const opponentXp = Number(
    (opponentSeat as { xp?: number } | undefined)?.xp ?? 0
  );
  const opponentLevel = getLevelFromXP(opponentXp);

  const show = !!((isGameOver || ended) && ready && (!dismissed || forceOpen));

  return (
    <Modal
      show={show}
      onClose={dismiss}
      closeOnBackdrop={false}
      title={isLocal ? 'Finished' : userWon ? 'Victory' : 'Defeat'}
      titleClassName={
        isLocal
          ? 'text-3xl font-extrabold uppercase tracking-[0.22em] text-bk-text md:text-4xl lg:text-5xl'
          : userWon
            ? 'animate-bk-pulse-soft text-3xl font-extrabold uppercase tracking-[0.22em] text-bk-gold-bright md:text-4xl lg:text-5xl'
            : 'text-3xl font-extrabold uppercase tracking-[0.22em] text-bk-danger-bright md:text-4xl lg:text-5xl'
      }
      bottom={(
        <>
          <div className="mt-5 flex flex-row flex-wrap justify-center gap-3">
            <ModalButton active onClick={dismiss} text="View Board" />
            <ModalButton isLink to={playAgainTo} text="Play Again" />
            <ModalButton isLink to="/" text="Home" />
          </div>
          <p className="mt-3 text-center text-[11px] text-bk-muted">
            Review the final position on the board. Replay &amp; fork coming soon.
          </p>
          <div className="mt-4 flex flex-row justify-center gap-3 border-t border-bk-border pt-4">
            <RedditShareButton url={shareUrl} title={shareTitle}>
              <RedditIcon size={28} round />
            </RedditShareButton>
            <TwitterShareButton
              url={shareUrl}
              title={shareTitle}
              hashtags={['BlindKingsChallenge']}
            >
              <TwitterIcon size={28} round />
            </TwitterShareButton>
            <FacebookMessengerShareButton
              url={shareUrl}
              appId="467535517673191"
              redirectUri="https://www.messenger.com/"
            >
              <FacebookMessengerIcon size={28} round />
            </FacebookMessengerShareButton>
            <FacebookShareButton url={shareUrl} hashtag="#BlindKingsChallenge">
              <FacebookIcon size={28} round />
            </FacebookShareButton>
            <WhatsappShareButton url={shareUrl} title={shareTitle} separator=":: ">
              <WhatsappIcon size={28} round />
            </WhatsappShareButton>
          </div>
        </>
      )}
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute right-3 top-3 z-10 border border-bk-border bg-bk-bg/80 p-1.5 text-bk-muted transition hover:border-bk-gold-dim hover:text-bk-gold"
        aria-label="Close results"
      >
        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
      </button>
      <div className="space-y-4 text-left">
        {!isLocal && opponentSeat ? (
          <div className="flex items-center justify-center gap-3 border border-bk-border bg-bk-bg px-3 py-3">
            <ProfileAvatar avatarId={opponentAvatar} size="sm" className="!h-10 !w-10" />
            <div className="min-w-0 text-left">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-bk-muted">
                {userWon ? 'Defeated' : 'Victor'}
              </div>
              <div className="truncate font-bold text-bk-text">{opponentName}</div>
              <div className="text-xs text-bk-gold">Level {opponentLevel}</div>
            </div>
            {winnerEmote ? (
              <div className="ml-auto">
                <EmoteIcon emoteId={winnerEmote} size="md" />
              </div>
            ) : null}
          </div>
        ) : winnerEmote ? (
          <div className="flex justify-center">
            <EmoteIcon emoteId={winnerEmote} size="lg" />
          </div>
        ) : null}

        <section>
          <SectionLabel>Progress</SectionLabel>
          <div className="border border-bk-border bg-bk-bg px-3 py-3">
            <div className="mb-3 flex items-baseline justify-between gap-2">
              <div className="text-lg font-bold text-bk-gold">Level {level}</div>
              {showedLevelUp ? (
                <div className="text-xs font-semibold uppercase tracking-wide text-bk-gold-bright">
                  Level up{levelsGained > 1 ? ` ×${levelsGained}` : ''}!
                </div>
              ) : null}
            </div>
            <div className="space-y-2">
              {Array.from({ length: progressBars }).map((_, i) => {
                const barLevel = previousLevel + i;
                const isCurrent = barLevel === level;
                return (
                  <div key={barLevel}>
                    <div
                      className={
                        isCurrent
                          ? 'mb-0.5 text-xs font-bold text-bk-gold'
                          : 'mb-0.5 text-xs text-bk-muted'
                      }
                    >
                      {barLevel}
                    </div>
                    <div className="relative flex h-2.5 overflow-hidden bg-bk-panel">
                      <div
                        className="absolute inset-y-0 left-0 bg-bk-accent transition-[width] duration-500"
                        style={{
                          width: `${getPercentageOfLevelFromXP(barLevel, currentXP)}%`,
                        }}
                      />
                      <div
                        className="absolute inset-y-0 left-0 bg-bk-gold transition-[width] duration-300"
                        style={{
                          width: `${getPercentageOfLevelFromXP(barLevel, previousXP)}%`,
                        }}
                      />
                    </div>
                    <div className="mt-0.5 flex justify-between text-[9px] text-bk-muted">
                      <span>{getXPForLevel(barLevel)} xp</span>
                      <span>{getXPForLevel(barLevel + 1) - 1} xp</span>
                    </div>
                  </div>
                );
              })}
            </div>
            {showedLevelUp && bonusGems > 0 ? (
              <div className="mt-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 border-t border-bk-border pt-3 text-sm">
                <span className="text-xs uppercase tracking-wide text-fuchsia-300">
                  Level-up bonus
                </span>
                <span className="inline-flex items-center gap-1 font-semibold tabular-nums text-fuchsia-200">
                  +{formatCurrencyAmount(bonusGems)} <Gem />
                </span>
                {bonusCoins > 0 ? (
                  <span className="inline-flex items-center gap-1 font-semibold tabular-nums text-bk-gold">
                    +{formatCurrencyAmount(bonusCoins)} <Coin />
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>

        <section>
          <SectionLabel>Match rewards</SectionLabel>
          {isLocal ? (
            <div className="border border-bk-border bg-bk-bg px-3 py-3 text-center">
              <div className="text-xs font-semibold uppercase tracking-wide text-bk-muted">
                Practice match
              </div>
              <p className="mt-1 text-xs text-bk-muted">
                Local games don&apos;t award XP or coins. Play online, ranked, or
                vs the bot to earn rewards.
              </p>
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <RewardTile
                  label="Coins"
                  amount={coinsEarned}
                  accentClass="text-bk-gold"
                  icon={<Coin />}
                  doubled={!!doubleCoins}
                  reserveBoostLine={!!(doubleXP || doubleCoins)}
                />
                <RewardTile
                  label="XP"
                  amount={xpEarned}
                  accentClass="text-sky-300"
                  icon={<span className="text-base font-semibold">XP</span>}
                  doubled={!!doubleXP}
                  reserveBoostLine={!!(doubleXP || doubleCoins)}
                />
              </div>
              {doubleXP && passXp > 0 ? (
                <p className="mt-2 text-center text-[11px] text-bk-muted">
                  Battle Pass +{formatCurrencyAmount(passXp)} XP
                  <span className="text-bk-muted/80"> (base, not doubled)</span>
                </p>
              ) : passXp > 0 ? (
                <p className="mt-2 text-center text-xs text-bk-muted">
                  Battle Pass +{formatCurrencyAmount(passXp)} XP
                </p>
              ) : null}
              {lengthNotice && !matchAbort ? (
                <div className="mt-2 border border-bk-gold-dim/40 bg-bk-bg px-3 py-2 text-center">
                  <div className="text-xs font-semibold uppercase tracking-wide text-bk-gold">
                    {lengthNotice.title}
                  </div>
                  <p className="mt-1 text-xs text-bk-muted">{lengthNotice.body}</p>
                </div>
              ) : null}
              {matchAbort ? (
                <div className="mt-2 border border-bk-border bg-bk-bg px-3 py-2 text-center">
                  <div className="text-xs font-semibold uppercase tracking-wide text-bk-muted">
                    Match aborted
                  </div>
                  <p className="mt-1 text-xs text-bk-muted">
                    Both players hadn&apos;t moved yet — no Elo or rewards. Likely a
                    connection drop.
                  </p>
                </div>
              ) : null}
            </>
          )}
        </section>

        {gameMode === GAME_MODES.RANKED_GAME &&
        (eloBefore != null || eloReason) ? (
          <section>
            <SectionLabel>Ranked Elo</SectionLabel>
            <div className="border border-bk-gold-dim/50 bg-bk-bg px-3 py-3 text-center">
              {eloRated && eloDelta != null ? (
                <>
                  <div className="text-2xl font-extrabold tabular-nums text-bk-gold-bright">
                    {eloDelta > 0 ? '+' : ''}
                    {eloDelta}
                  </div>
                  <p className="mt-1 text-sm text-bk-text">
                    {eloBefore} → {eloAfter}
                    {rewardTier ? (
                      <span className="text-bk-muted">
                        {' '}
                        · {String(rewardTier).replace('_', ' ')}
                      </span>
                    ) : null}
                  </p>
                </>
              ) : (
                <>
                  <div className="text-sm font-bold uppercase tracking-wide text-bk-muted">
                    No Elo change
                  </div>
                  <p className="mt-1 text-xs text-bk-muted">
                    {eloReason === 'rematch'
                      ? 'Unrated — rematch within 24 hours.'
                      : eloReason === 'too_short'
                        ? 'Unrated — game too short.'
                        : eloReason === 'abort'
                          ? 'Unrated — aborted before both players moved.'
                          : 'This match did not affect your rating.'}
                  </p>
                  {eloBefore != null ? (
                    <p className="mt-1 text-xs tabular-nums text-bk-gold">
                      Rating {eloBefore}
                    </p>
                  ) : null}
                </>
              )}
            </div>
          </section>
        ) : null}
      </div>
    </Modal>
  );
}
